import api from './api';

export interface Translation {
  key_path: string;
  translation_text: string;
  status: string;
  language_name: string;
  created_at?: string;
  updated_at?: string;
}

export interface TranslationKey {
  id: string;
  key_path: string;
  description: string;
  category: string;
  component: string;
  translations: Record<string, Translation>;
}

export interface Language {
  id: string;
  language_code: string;
  language_name: string;
  native_name: string;
  is_rtl: boolean;
  is_default: boolean;
  is_active: boolean;
}

export interface TranslationCategory {
  id: string;
  name: string;
  description: string;
  key_count: number;
}

export interface TranslationStatus {
  language_code: string;
  language_name: string;
  total_keys: number;
  translated_keys: number;
  published_keys: number;
  approved_keys: number;
  review_keys: number;
  draft_keys: number;
  completion_percentage: number;
}

class TranslationService {
  private cache: Map<string, Record<string, string>> = new Map();
  private cacheTimestamp: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes (industry standard)

  // Get translations for a specific language with optional page filtering
  async getTranslationsByLanguage(
    languageCode: string, 
    page?: string, 
    keys?: string[],
    forceRefresh: boolean = false
  ): Promise<Record<string, string>> {
    const cacheKey = `translations_${languageCode}_${page || 'all'}_${keys?.join(',') || 'all'}`;
    const now = Date.now();
    
    // Check cache first (skip if forceRefresh is true)
    if (!forceRefresh && this.cache.has(cacheKey)) {
      const timestamp = this.cacheTimestamp.get(cacheKey) || 0;
      if (now - timestamp < this.CACHE_DURATION) {
        return this.cache.get(cacheKey) || {};
      }
    }

    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (page) params.append('page', page);
      if (keys && keys.length > 0) params.append('keys', keys.join(','));
      
      const url = `/translations/${languageCode}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);
      
      if (response.data.success) {
        const translations = response.data.translations;
        
        // Cache the translations
        this.cache.set(cacheKey, translations);
        this.cacheTimestamp.set(cacheKey, now);
        
        console.log(`📦 Loaded ${response.data.count} translations for ${languageCode}${page ? ` (page: ${page})` : ''}`);
        
        return translations;
      } else {
        console.error('Failed to fetch translations:', response.data.message);
        return {};
      }
    } catch (error) {
      console.error('Error fetching translations:', error);
      return {};
    }
  }

  // Get all translations for admin interface
  async getAllTranslations(): Promise<TranslationKey[]> {
    try {
      const response = await api.get('/translations/admin/all');
      
      if (response.data.success) {
        return response.data.translations;
      } else {
        console.error('Failed to fetch all translations:', response.data.message);
        return [];
      }
    } catch (error) {
      console.error('Error fetching all translations:', error);
      return [];
    }
  }

  // Create or update a translation
  async upsertTranslation(
    keyPath: string, 
    languageCode: string, 
    translationText: string, 
    status: string = 'draft'
  ): Promise<boolean> {
    try {
      const response = await api.post('/translations/admin/upsert', {
        keyPath,
        languageCode,
        translationText,
        status
      });
      
      if (response.data.success) {
        // Clear cache for this language
        this.clearCacheForLanguage(languageCode);
        return true;
      } else {
        console.error('Failed to save translation:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('Error saving translation:', error);
      return false;
    }
  }

  // Get translation status for a language
  async getTranslationStatus(languageCode: string): Promise<TranslationStatus | null> {
    try {
      const response = await api.get(`/translations/status/${languageCode}`);
      
      if (response.data.success) {
        return response.data.status;
      } else {
        console.error('Failed to fetch translation status:', response.data.message);
        return null;
      }
    } catch (error) {
      console.error('Error fetching translation status:', error);
      return null;
    }
  }

  // Get all supported languages
  async getLanguages(): Promise<Language[]> {
    try {
      const response = await api.get('/translations/languages/all');
      
      if (response.data.success) {
        return response.data.languages;
      } else {
        console.error('Failed to fetch languages:', response.data.message);
        return [];
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      return [];
    }
  }

  // Get translation categories
  async getTranslationCategories(): Promise<TranslationCategory[]> {
    try {
      const response = await api.get('/translations/admin/categories');
      
      if (response.data.success) {
        return response.data.categories;
      } else {
        console.error('Failed to fetch translation categories:', response.data.message);
        return [];
      }
    } catch (error) {
      console.error('Error fetching translation categories:', error);
      return [];
    }
  }

  // Delete a translation
  async deleteTranslation(keyPath: string, languageCode: string): Promise<boolean> {
    try {
      const response = await api.delete(`/translations/admin/${keyPath}/${languageCode}`);
      
      if (response.data.success) {
        // Clear cache for this language
        this.clearCacheForLanguage(languageCode);
        return true;
      } else {
        console.error('Failed to delete translation:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('Error deleting translation:', error);
      return false;
    }
  }

  // Bulk import translations
  async bulkImportTranslations(
    translations: Record<string, string>, 
    languageCode: string
  ): Promise<{ success: boolean; message: string; results?: any[] }> {
    try {
      const response = await api.post('/translations/admin/bulk-import', {
        translations,
        languageCode
      });
      
      if (response.data.success) {
        // Clear cache for this language
        this.clearCacheForLanguage(languageCode);
        return {
          success: true,
          message: response.data.message,
          results: response.data.results
        };
      } else {
        return {
          success: false,
          message: response.data.message
        };
      }
    } catch (error) {
      console.error('Error in bulk import:', error);
      return {
        success: false,
        message: 'Failed to bulk import translations'
      };
    }
  }

  // Clear cache for a specific language
  private clearCacheForLanguage(languageCode: string): void {
    const cacheKey = `translations_${languageCode}`;
    this.cache.delete(cacheKey);
    this.cacheTimestamp.delete(cacheKey);
  }

  // Clear all cache
  clearCache(): void {
    this.cache.clear();
    this.cacheTimestamp.clear();
  }

  // Get cached translations for a language
  getCachedTranslations(languageCode: string): Record<string, string> | null {
    const cacheKey = `translations_${languageCode}`;
    const timestamp = this.cacheTimestamp.get(cacheKey) || 0;
    const now = Date.now();
    
    if (now - timestamp < this.CACHE_DURATION && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) || null;
    }
    
    return null;
  }
}

export default new TranslationService();
